import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import type { Professional, StaffUser } from "../types";
import DashboardTab from "../components/ejecutivo/DashboardTab";
import AlertsPanel from "../components/ejecutivo/AlertsPanel";
import ForecastPanel from "../components/ejecutivo/ForecastPanel";

// Roles per §14 of MVP_E_SPEC.md / the contract's permissions summary:
// director/admin/sales_manager -> everything; marketing -> campaigns/
// funnel/cohorts aggregated (no individual commission/settlement, which
// the backend already omits from its response — see hideKeys below as a
// belt-and-suspenders frontend guard so a stray field never renders even
// if the backend ever slips); sales/professional -> own scorecard only,
// no cross-tab access, no other-person selector (tests #26-28).
const FULL_ROLES = ["admin", "director", "sales_manager"];
const MARKETING_ROLES = [...FULL_ROLES, "marketing"];

type TabKey = "resumen" | "funnel" | "marketing" | "vendedoras" | "profesionales" | "tratamientos" | "finanzas" | "ltv" | "forecast" | "alertas";

interface TabDef {
  key: TabKey;
  label: string;
  roles: string[];
}

const ALL_TABS: TabDef[] = [
  { key: "resumen", label: "Resumen ejecutivo", roles: MARKETING_ROLES },
  { key: "funnel", label: "Funnel", roles: MARKETING_ROLES },
  { key: "marketing", label: "Marketing / Atribución", roles: MARKETING_ROLES },
  // Marketing gets dashboard/sellers too, but aggregated only and without
  // commission fields (test #26) — the per-seller selector below is
  // hidden for that role so it can't pull one seller's individual detail.
  { key: "vendedoras", label: "Vendedoras", roles: [...MARKETING_ROLES, "sales"] },
  // Professional-level detail isn't in marketing's explicit scope (§14
  // only lists campañas/funnels/cohortes agregadas for marketing), so this
  // tab stays director/admin/sales_manager + the professional's own view.
  { key: "profesionales", label: "Profesionales", roles: [...FULL_ROLES, "professional"] },
  { key: "tratamientos", label: "Tratamientos", roles: MARKETING_ROLES },
  { key: "finanzas", label: "Finanzas / BCM", roles: FULL_ROLES },
  { key: "ltv", label: "LTV / Cohortes", roles: MARKETING_ROLES },
  { key: "forecast", label: "Forecast", roles: FULL_ROLES },
  { key: "alertas", label: "Alertas", roles: MARKETING_ROLES },
];

// Fields the contract explicitly says marketing must never see (test #26):
// individual commission/settlement/attributed_bcm figures. Kept as a
// frontend safety net on top of the backend's own field omission.
const MARKETING_HIDE = ["commission", "base_commission", "bonuses", "total_due", "attributed_bcm", "seller_commission", "commissions"];

export default function Ejecutivo() {
  const { user } = useAuth();
  const role = user?.role_code || "";
  const isSalesOnly = role === "sales";
  const isProfessionalOnly = role === "professional";
  const isMarketing = role === "marketing";

  const visibleTabs = useMemo(() => {
    if (isSalesOnly) return ALL_TABS.filter((t) => t.key === "vendedoras");
    if (isProfessionalOnly) return ALL_TABS.filter((t) => t.key === "profesionales");
    return ALL_TABS.filter((t) => t.roles.includes(role));
  }, [role, isSalesOnly, isProfessionalOnly]);

  const [tab, setTab] = useState<TabKey>(visibleTabs[0]?.key || "resumen");
  useEffect(() => {
    if (!visibleTabs.find((t) => t.key === tab)) setTab(visibleTabs[0]?.key || "resumen");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTabs.map((t) => t.key).join(",")]);

  const [sellers, setSellers] = useState<StaffUser[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [sellerId, setSellerId] = useState<string>(isSalesOnly ? String(user?.id || "") : "");
  const [professionalId, setProfessionalId] = useState<string>("");

  useEffect(() => {
    if (FULL_ROLES.includes(role) || isMarketing) {
      api.users().then(setSellers).catch(() => setSellers([]));
      api.getProfessionals().then(setProfessionals).catch(() => setProfessionals([]));
    }
  }, [role, isMarketing]);

  if (!user) return null;

  if (visibleTabs.length === 0) {
    return (
      <div className="dashboard-page">
        <h1>Centro de control ejecutivo</h1>
        <p className="meta">No tenés permisos para ver el dashboard ejecutivo.</p>
      </div>
    );
  }

  const marketingHide = isMarketing ? MARKETING_HIDE : undefined;

  return (
    <div className="dashboard-page">
      <h1>Centro de control ejecutivo</h1>
      <p className="meta">
        Dashboard unificado de MVP-A a MVP-D: funnel, atribución de marketing, performance de equipo, finanzas, LTV, forecast de
        pipeline y alertas. Cada número es reproducible desde los registros fuente — hacé click para ver el detalle.
      </p>

      <div className="pipeline-tabs" style={{ flexWrap: "wrap" }}>
        {visibleTabs.map((t) => (
          <button key={t.key} className={`tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        {tab === "resumen" && <DashboardTab fetcher={api.getExecutiveDashboard} hideKeys={marketingHide} />}

        {tab === "funnel" && <DashboardTab fetcher={api.getFunnelDashboard} hideKeys={marketingHide} />}

        {tab === "marketing" && <DashboardTab fetcher={api.getMarketingDashboard} hideKeys={marketingHide} />}

        {tab === "vendedoras" && (
          <DashboardTab
            fetcher={api.getSellersDashboard}
            hideKeys={isMarketing ? MARKETING_HIDE : undefined}
            extraParams={{ seller_id: isSalesOnly ? String(user.id) : !isMarketing ? sellerId || undefined : undefined }}
            extraControls={
              isSalesOnly ? (
                <span className="meta">Mostrando tu propio scorecard.</span>
              ) : isMarketing ? (
                <span className="meta">Vista agregada (Marketing no accede al detalle individual de comisiones).</span>
              ) : (
                <label className="meta">
                  Vendedora
                  <select value={sellerId} onChange={(e: any) => setSellerId(e.target.value)}>
                    <option value="">Todas (agregado)</option>
                    {sellers
                      .filter((s) => ["sales", "sales_manager"].includes(s.role_code))
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.full_name}
                        </option>
                      ))}
                  </select>
                </label>
              )
            }
          />
        )}

        {tab === "profesionales" && (
          <DashboardTab
            fetcher={api.getProfessionalsDashboard}
            extraParams={{ professional_id: isProfessionalOnly ? String(user.id) : professionalId || undefined }}
            extraControls={
              !isProfessionalOnly ? (
                <label className="meta">
                  Profesional
                  <select value={professionalId} onChange={(e: any) => setProfessionalId(e.target.value)}>
                    <option value="">Todos (agregado)</option>
                    {professionals.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.full_name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <span className="meta">Mostrando tu propio scorecard.</span>
              )
            }
          />
        )}

        {tab === "tratamientos" && <DashboardTab fetcher={api.getTreatmentsDashboard} hideKeys={marketingHide} />}

        {tab === "finanzas" && <DashboardTab fetcher={api.getFinanceDashboard} />}

        {tab === "ltv" && <DashboardTab fetcher={api.getLtvEDashboard} hideKeys={marketingHide} />}

        {tab === "forecast" && <ForecastPanel canEditProbabilities={FULL_ROLES.includes(role)} />}

        {tab === "alertas" && <AlertsPanel canAcknowledge={FULL_ROLES.includes(role)} />}
      </div>
    </div>
  );
}
